import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import useReveal from '../AIEmployees/useReveal';
import '../AIEmployees/sections/AIEFaq.css';
import { COST_FAQ_ANSWER } from '../../content/pricingFigures';
import { BENCHMARK_LABEL, BENCHMARK_MONTH } from '../../content/benchmarkClaim';

/**
 * HomeFaq — the homepage FAQ band (mock v4, Aug 6; product scope Aug 7).
 *
 * Two jobs, same pattern as AIEFaq:
 *  1. UX: the six questions a stranger actually asks on the homepage.
 *  2. GEO/SEO: HomePage renders FAQPage JSON-LD from THIS exact list
 *     (HOME_FAQ_ITEMS), so structured data always matches visible content.
 *
 * SCOPE (Aug 7, Nitish): the homepage FAQ covers ALL THREE products — the
 * first pass was six AI-employee questions, which duplicated /ai-employees.
 * Now: three employee anchors (what is it / cost / control) + one each for
 * Super Agent and Agent-to-Agent + the connect-your-own-tools question.
 * Employee-depth questions (roles, away-work, chatbot contrast) live on
 * /ai-employees. Every claim is grounded in real product behavior. Copy
 * through Rhea's SEO pass (her lane).
 * Reuses the .aie-faq__* styles from AIEFaq.css.
 */
export const HOME_FAQ_ITEMS = [
  {
    question: 'What is an AI employee?',
    answer:
      'A general-purpose AI worker you hire for a role. They show up with a name, their own email inbox, a schedule of shifts, persistent memory, and a task board. You set the goals and permissions; they plan their own work and report back every shift.',
  },
  {
    question: 'How much is an AI employee?',
    answer:
      COST_FAQ_ANSWER,
  },
  {
    /* Round-6 (Aug 17 ruling): NO PRICE IN THE FOLD — a shift is undefined at
       first sight, so the cost answer lives here, where this entry gives it
       the room it needs. Sits directly after the cost question it explains. */
    question: 'What is a shift?',
    answer:
      'A shift is one block of focused work, not an eight-hour day. Your employee wakes on a schedule or on a signal, works through what they planned, and ends with a report back to you. The next shift picks up where the last one left off, because they remember.',
  },
  {
    question: 'How do I control what they are allowed to do?',
    answer:
      'You set the permissions up front. You decide what they can do on their own, and anything dangerous waits for your approval. Every task and email they touch is visible to you on one board.',
  },
  {
    question: 'What is the Super Agent?',
    answer:
      `The deep-reasoning engine every AI employee runs on, and a product in its own right. Give it one prompt and it plans the work, researches with cited sources, and delivers a finished artifact: reports, PDFs, presentations, spreadsheets, dashboards, web apps, video, audio, images, and code. It ranked ${BENCHMARK_LABEL}.`,
    /* Rendered variant linking the benchmark receipt (Rhea SEO pass, Aug 7:
       the #1 DRB claim is always date-stamped + linked). JSON-LD keeps
       reading the plain `answer` string above. */
    answerJsx: (
      <>
        The deep-reasoning engine every AI employee runs on, and a product in
        its own right. Give it one prompt and it plans the work, researches
        with cited sources, and delivers a finished artifact: reports, PDFs,
        presentations, spreadsheets, dashboards, web apps, video, audio,
        images, and code. It ranked #1 on{' '}
        <Link to="/benchmarks">DeepResearch Bench</Link> ({BENCHMARK_MONTH}).
      </>
    ),
  },
  {
    question: 'What is agent-to-agent?',
    answer:
      'CellCog built for other agents, not just humans. Agents and coding tools like OpenClaw, Claude Code, Cursor, and Codex can start chats, delegate whole jobs, and collect finished artifacts through the CellCog API, and the Linear integration lets CellCog agents pick up your issues. Around 30% of chats on CellCog are already started by other agents.',
  },
  {
    question: 'Do all three products share the same credits?',
    answer:
      'Yes. One credit balance powers everything: AI employee shifts, Super Agent chats, and agent-to-agent calls all draw from the same credits. You start with 200 free credits, no credit card needed, and when you need more you pick a plan or top up as you go. Full details are on the pricing page.',
    /* Rendered variant with the pricing page interlinked (Aug 7, Nitish).
       JSON-LD keeps reading the plain `answer` string above. */
    answerJsx: (
      <>
        Yes. One credit balance powers everything: AI employee shifts, Super
        Agent chats, and agent-to-agent calls all draw from the same credits.
        You start with 200 free credits, no credit card needed, and when you
        need more you pick a plan or top up as you go. Full details are on
        the <Link to="/pricing">pricing page</Link>.
      </>
    ),
  },
];

const HomeFaq = () => {
  const [ref, visible] = useReveal();
  const [open, setOpen] = useState(null);

  const toggle = (i) => setOpen((cur) => (cur === i ? null : i));

  return (
    <section className="aie-faq aie-section" ref={ref} aria-labelledby="home-faq-title">
      <div className={`aie-section-head aie-reveal ${visible ? 'is-visible' : ''}`}>
        <span className="aie-eyebrow">FAQ</span>
        <h2 id="home-faq-title" className="aie-section-title aie-display">
          Questions, answered.
        </h2>
      </div>

      <div className={`aie-faq__list aie-reveal ${visible ? 'is-visible' : ''}`}>
        {HOME_FAQ_ITEMS.map((item, i) => (
          <div
            key={item.question}
            className={`aie-faq__item ${open === i ? 'is-open' : ''}`}
          >
            <button
              type="button"
              className="aie-faq__q"
              aria-expanded={open === i}
              onClick={() => toggle(i)}
            >
              <span>{item.question}</span>
              <PlusOutlined className="aie-faq__icon" />
            </button>
            <div className="aie-faq__a-wrap" role="region">
              <p className="aie-faq__a">{item.answerJsx || item.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default HomeFaq;
